import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import ExamCooldownBanner from "@/components/exam/ExamCooldownBanner";
import ExamHistoryPanel from "@/components/exam/ExamHistoryPanel";
import ExamIntroCard from "@/components/exam/ExamIntroCard";
import ExamResultsCard from "@/components/exam/ExamResultsCard";
import ExamSession from "@/components/exam/ExamSession";
import LearnerPortalShell from "@/components/learner/LearnerPortalShell";
import { track } from "@/lib/analytics";
import { ApiError } from "@/lib/api";
import type { UsStateCode } from "@/lib/states";
import {
  ExamBankTooSmallError,
  ExamCooldownError,
  ExamSessionInProgressError,
  getExamConfig,
  getExamHistory,
  startExam,
  submitExam,
  type ExamConfigResponse,
  type ExamHistoryEntry,
  type ExamQuestionForClient,
  type StartExamResponse,
  type SubmitExamPayload,
  type SubmitExamResponse,
} from "@/lib/exam-api";

/**
 * apps/web :: pages :: ExamPage
 *
 * The /exam/:stateCode route. Mounted inside <ProtectedRoute><OnboardingGate>
 * (see App.tsx), so the caller is guaranteed authed + onboarded.
 *
 * Four visual states drive the layout:
 *
 *   1. config-idle   → `<ExamIntroCard>` (pre-start) or cooldown banner.
 *   2. active        → `<ExamSession>` (timer + question loop).
 *   3. results       → `<ExamResultsCard>` (score + review).
 *   4. redirect      → server rejected the start (403 state not in
 *                      targets, or profile missing).
 *
 * Error handling:
 *
 *   - 403 state_not_in_targets → redirect to /dashboard.
 *   - 409 bank_too_small       → show bank-insufficient intro card.
 *   - 409 session_in_progress  → currently surfaces as ApiError (TODO:
 *                                expose resume flow post-MVP).
 *   - 429 cooldown             → show ExamCooldownBanner.
 *
 * Re-fetching: on cooldown-expiry we re-run config + history so the user
 * can re-enter. The retry button in the results card re-invokes start.
 */

type LoadState =
  | { kind: "loading" }
  | {
      kind: "idle";
      config: ExamConfigResponse;
      history: ExamHistoryEntry[];
      cooldownUntilMs: number | null;
    }
  | {
      kind: "active";
      config: ExamConfigResponse;
      start: StartExamResponse;
    }
  | {
      kind: "results";
      config: ExamConfigResponse;
      questions: ExamQuestionForClient[];
      result: SubmitExamResponse;
    }
  | { kind: "redirect-dashboard"; toastMessage: string }
  | { kind: "error"; message: string };

function toMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}

/**
 * Derive the cooldown deadline from the most recent FAILED attempt.
 * The backend's spec.retryCooldownMinutes is the canonical source, so
 * we extend (submittedAtMs + cooldownMin * 60_000) and expose that as
 * the "retryAvailableAtMs" the banner renders.
 */
function cooldownUntilMs(
  attempts: ExamHistoryEntry[],
  cooldownMinutes: number,
): number | null {
  if (attempts.length === 0) return null;
  const latest = attempts[0];
  if (latest.passed) return null;
  const candidate = latest.submittedAtMs + cooldownMinutes * 60_000;
  if (candidate <= Date.now()) return null;
  return candidate;
}

export default function ExamPage() {
  const { t } = useTranslation();
  const { stateCode: raw } = useParams<{ stateCode: string }>();
  const stateCode = (raw ?? "").toUpperCase();
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  // Analytics: epoch-ms when the current session became active. Set in
  // handleStart on successful startExam; read in handleSubmit to compute
  // `time_spent_ms`. Kept in a ref so it doesn't cause extra renders.
  const sessionStartedAtMsRef = useRef<number>(0);
  // attemptCount snapshot at start time — lets us compute attempt_number
  // for the analytics event without waiting on a history refetch.
  const attemptCountAtStartRef = useRef<number>(0);

  const loadIdle = useCallback(async () => {
    setState({ kind: "loading" });
    try {
      const [config, history] = await Promise.all([
        getExamConfig(stateCode),
        getExamHistory(stateCode, 10),
      ]);
      const until = cooldownUntilMs(
        history.attempts,
        config.spec.retryCooldownMinutes,
      );
      setState({
        kind: "idle",
        config,
        history: history.attempts,
        cooldownUntilMs: until,
      });
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setState({
          kind: "redirect-dashboard",
          toastMessage: t("exam.toast.stateNotInTargets"),
        });
        return;
      }
      setState({ kind: "error", message: toMessage(err) });
    }
  }, [stateCode, t]);

  useEffect(() => {
    if (!stateCode) return;
    void loadIdle();
  }, [stateCode, loadIdle]);

  const handleStart = useCallback(async () => {
    if (state.kind !== "idle") return;
    try {
      const start = await startExam(stateCode);
      // Analytics: record start time + attempt count BEFORE flipping to
      // active so handleSubmit can compute time_spent_ms and the attempt
      // number deterministically. `attemptCountAtStartRef` is the number
      // of attempts before this one; +1 at submit time gives the current
      // attempt_number.
      sessionStartedAtMsRef.current = start.startedAtMs;
      attemptCountAtStartRef.current = state.history.length;
      track("exam.started", {
        state: stateCode as UsStateCode,
        question_count: state.config.spec.questionCount,
        time_limit_seconds: state.config.spec.timeLimitSeconds,
        pass_percent: state.config.spec.passPercent,
      });
      setState({ kind: "active", config: state.config, start });
    } catch (err) {
      if (err instanceof ExamCooldownError) {
        setState({
          kind: "idle",
          config: state.config,
          history: state.history,
          cooldownUntilMs: err.retryAvailableAtMs,
        });
        return;
      }
      if (err instanceof ExamBankTooSmallError) {
        // The config+history already drive the intro; surface a static
        // banner via the bankInsufficient path on the next render by
        // flipping the config's bankSufficient flag.
        setState({
          kind: "idle",
          config: {
            ...state.config,
            bankSufficient: false,
            bankAvailable: err.available,
          },
          history: state.history,
          cooldownUntilMs: null,
        });
        return;
      }
      if (err instanceof ExamSessionInProgressError) {
        setState({
          kind: "error",
          message: t("exam.errors.sessionInProgress"),
        });
        return;
      }
      if (err instanceof ApiError && err.status === 403) {
        setState({
          kind: "redirect-dashboard",
          toastMessage: t("exam.toast.stateNotInTargets"),
        });
        return;
      }
      setState({ kind: "error", message: toMessage(err) });
    }
  }, [state, stateCode, t]);

  const handleSubmit = useCallback(
    async (
      payload: SubmitExamPayload,
      meta: { autoSubmittedOnTimeout: boolean },
    ) => {
      if (state.kind !== "active") return;
      try {
        const result = await submitExam(payload);
        // Analytics: emit exam.submitted once the server has accepted the
        // submission. `time_spent_ms` is wall-clock from start → now (not
        // the backend's server-side delta, which is close but not
        // identical). `attempt_number` is 1-based.
        track("exam.submitted", {
          state: stateCode as UsStateCode,
          passed: result.passed,
          correct_count: result.correctCount,
          question_count: result.questionCount,
          pass_percent: result.passPercent,
          time_spent_ms: Math.max(
            0,
            Date.now() - sessionStartedAtMsRef.current,
          ),
          attempt_number: attemptCountAtStartRef.current + 1,
          auto_submitted_on_timeout: meta.autoSubmittedOnTimeout,
        });
        setState({
          kind: "results",
          config: state.config,
          questions: state.start.questions,
          result,
        });
      } catch (err) {
        setState({ kind: "error", message: toMessage(err) });
      }
    },
    [state, stateCode],
  );

  const handleRetry = useCallback(() => {
    void loadIdle();
  }, [loadIdle]);

  const historyBelow = useMemo(() => {
    if (state.kind !== "idle") return null;
    if (state.history.length === 0) return null;
    return (
      <ExamHistoryPanel
        stateCode={stateCode}
        attempts={state.history}
      />
    );
  }, [state, stateCode]);

  // Page title shown in the LearnerPortalShell header bar — short, matches
  // the sidebar nav label (`nav.exam` = "Exam mode" / "Modo examen").
  const pageTitle = t("nav.exam");

  if (state.kind === "loading") {
    return (
      <LearnerPortalShell pageTitle={pageTitle}>
        <div
          role="status"
          aria-live="polite"
          data-testid="exam-page-loading"
          className="mx-auto mt-xl max-w-md p-md text-center text-neutral-700"
        >
          {t("exam.pageLoading")}
        </div>
      </LearnerPortalShell>
    );
  }

  if (state.kind === "redirect-dashboard") {
    return (
      <Navigate
        to="/dashboard"
        replace
        state={{ toast: state.toastMessage }}
      />
    );
  }

  if (state.kind === "error") {
    return (
      <LearnerPortalShell pageTitle={pageTitle}>
        <section className="mx-auto max-w-3xl px-md pt-xl pb-xl sm:px-lg">
          <div
            role="alert"
            data-testid="exam-page-error"
            className="rounded-md border border-danger/40 bg-danger/5 p-md text-sm text-danger"
          >
            {t("exam.pageError", { message: state.message })}
          </div>
          <button
            type="button"
            data-testid="exam-page-retry"
            onClick={() => void loadIdle()}
            className="mt-md inline-flex items-center rounded-md border border-brand-primary bg-brand-primary px-md py-sm text-sm font-semibold text-white transition-colors hover:bg-brand-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
          >
            {t("exam.retry")}
          </button>
        </section>
      </LearnerPortalShell>
    );
  }

  if (state.kind === "active") {
    return (
      <LearnerPortalShell pageTitle={pageTitle}>
        <section className="mx-auto max-w-3xl px-md pt-xl pb-xl sm:px-lg">
          <ExamSession
            sessionId={state.start.sessionId}
            questions={state.start.questions}
            deadlineMs={state.start.deadlineMs}
            onSubmit={handleSubmit}
          />
        </section>
      </LearnerPortalShell>
    );
  }

  if (state.kind === "results") {
    return (
      <LearnerPortalShell pageTitle={pageTitle}>
        <section className="mx-auto max-w-3xl px-md pt-xl pb-xl sm:px-lg">
          <ExamResultsCard
            stateCode={stateCode}
            result={state.result}
            questions={state.questions}
            onRetry={handleRetry}
          />
        </section>
      </LearnerPortalShell>
    );
  }

  // idle.
  const bankInsufficient = !state.config.bankSufficient;
  return (
    <LearnerPortalShell pageTitle={pageTitle}>
      <section className="mx-auto max-w-3xl px-md pt-xl pb-xl sm:px-lg sm:pt-2xl sm:pb-2xl">
        {state.cooldownUntilMs != null ? (
          <ExamCooldownBanner retryAvailableAtMs={state.cooldownUntilMs} />
        ) : (
          <ExamIntroCard
            stateCode={stateCode}
            spec={state.config.spec}
            bankInsufficient={bankInsufficient}
            bankAvailable={state.config.bankAvailable}
            onStart={handleStart}
          />
        )}
        {historyBelow}
      </section>
    </LearnerPortalShell>
  );
}
